// Terms of Use for a free service operated from Germany: accounts, the CC BY 4.0 grant, Wizard
// marking, automatic checks, reports, limits, prohibited content, liability, termination.
import { Link } from "../../lib/router";
import { CONTACT, LegalLayout, LegalLink } from "./LegalLayout";

export function Terms() {
  return (
    <LegalLayout slug="terms" title="Terms of Use">
      <h2 id="scope">1. Who we are and what these terms cover</h2>
      <p>
        The Infinite is operated by The Infinite Beat Project G47, Berlin, which is in founding stage (“we”, “us”;
        details in the <LegalLink to="imprint">Imprint</LegalLink>). These terms apply to everyone who uses The
        Infinite. Listening needs no account. By creating an account you agree to these terms. How we handle personal
        data is explained in the <LegalLink to="privacy">Privacy Policy</LegalLink>.
      </p>

      <h2 id="service">2. The service</h2>
      <p>
        The Infinite is one song that never stops (with a slower sister stream, The Infinite 72). It plays at 125 BPM
        (72 BPM) for everyone at the same time, following a world clock. Each day has its own key, sounds and lineup.
        Contributors write 8-bar loops for a day; each accepted loop gets a slot in that day's lineup, where it plays
        twice followed by a break bar. Free slots are filled by loops the Wizard generates (“Wizard fillers”). Past days
        can be replayed.
      </p>
      <p>
        The Infinite is free of charge. There is no right to have a loop accepted, to a particular slot or play time, or
        to any feature remaining as it is.
      </p>

      <h2 id="accounts">3. Accounts</h2>
      <ul>
        <li>You must be at least 16 years old to create an account.</li>
        <li>
          You sign in with links sent to your email address. Keep access to that mailbox safe; anyone who can read it
          can sign in as you.
        </li>
        <li>
          One person, one account. Creating several accounts to get around limits, to inflate reports or to evade a
          suspension is not allowed.
        </li>
        <li>
          Your nickname is public and credits your loops. It must not impersonate someone else, and it must not be
          offensive or infringe anyone's rights (for example a trademark). Nicknames are checked automatically.
        </li>
      </ul>

      <h2 id="contributions">4. Your songs and the licence you grant</h2>
      <p>When you submit a song, you confirm that:</p>
      <ul>
        <li>
          the loop is <strong>your own work</strong> — you wrote it yourself, possibly with help from the Wizard — and
          it doesn't copy a melody, riff or other material from someone else;
        </li>
        <li>you hold the rights needed to license it, and its title doesn't infringe anyone's rights.</li>
      </ul>
      <p>
        You license each submitted loop (its notes, settings and title) under the{" "}
        <strong>Creative Commons Attribution 4.0 International licence (CC BY 4.0)</strong>, credited to your nickname.
        This lets us and anyone else copy, play, record, adapt and share it, also commercially, as long as they give
        credit. The licence is irrevocable: it continues after you delete your account or after these terms end. Details
        and the attribution format are on the <LegalLink to="licence">Licence</LegalLink> page.
      </p>
      <p>
        When you delete your account, we treat this as your request under CC BY 4.0 (section 3(a)(3)) to remove your
        name: loops that have already played stay in the song credited to “former contributor”, and loops that have not
        played yet are removed and replaced by Wizard fillers.
      </p>
      <p>
        You remain responsible for what you contribute. If a loop infringes someone else's rights, you must tell us
        without delay.
      </p>

      <h2 id="wizard">5. The Wizard and its mark</h2>
      <p>
        The Wizard can suggest chords, bass lines, melodies, arpeggios, harmonies, fills or grooves in the day's key and
        sounds. Every suggestion you receive is logged. When you submit a loop, our server compares it with all Wizard
        suggestions you received for that day and computes the share of notes that came from the Wizard. That mark (for
        example “38 % Wizard”) is stored with the loop for good and always shown with it. It can't be switched off or
        removed. Trying to get around it is not allowed.
      </p>

      <h2 id="checks">6. Automatic checks, limits and slots</h2>
      <ul>
        <li>
          Every submitted loop is checked automatically: structure, key and sounds, note density, loudness and noise,
          and similarity to earlier loops and to a list of protected melodies. A loop that fails is rejected with a
          reason; you can change it and submit it again. Accepted loops are loudness-matched so that everyone sits at
          the same level.
        </li>
        <li>
          Current limits: up to 3 loops per day and 10 loops per 7 days per account; a draft reserves a slot of its day
          for 3 hours while you work on it. We may change limits and thresholds to keep the song working.
        </li>
        <li>
          Once a day's lineup is fixed, the order is final. Plays happen at the time the world clock says; we can't move
          a loop to suit you.
        </li>
      </ul>

      <h2 id="reports">7. Reports, hiding and complaints</h2>
      <p>
        Every loop can be reported (copied melody, offensive title or nickname, noise or abuse, or other). When reports
        from 3 different accounts that are at least 7 days old come in, the loop is hidden from all future plays at once
        and replaced by a filler. It is then re-checked automatically with stricter thresholds: if it fails it stays
        hidden, otherwise it is restored. Reporting a loop falsely or in bad faith, or coordinating reports, is not
        allowed.
      </p>
      <p>
        We also act on notices about illegal content sent to us via the official accounts (tba). If we hide or remove
        your song or restrict your account, we tell you why, unless the law forbids it. If you disagree with a decision
        — an automatic one or ours — {CONTACT}; a person will look at it and answer.
      </p>

      <h2 id="prohibited">8. What is not allowed</h2>
      <ul>
        <li>
          Loops that copy other people's melodies or other protected material, or that you otherwise have no right to
          license.
        </li>
        <li>
          Titles or nicknames that are insulting, hateful, discriminatory, sexually explicit, violent, or that advertise
          something or impersonate someone.
        </li>
        <li>Content that is illegal, in particular under German or EU law.</li>
        <li>Noise walls, deliberate harshness or other songs meant to spoil the song for listeners.</li>
        <li>
          Automated submissions, scraping that puts load on the service, attacks, probing for security holes without our
          agreement, or circumventing limits, checks or the Wizard mark.
        </li>
        <li>Misusing the report function or the sign-in email function (for example to flood someone's inbox).</li>
      </ul>
      <p>
        If you break these rules, we may hide or remove the content concerned, temporarily block submissions or reports
        from your account, or close it. We choose the mildest measure that is enough, taking into account how serious
        and how frequent the violation is and whether it was intentional.
      </p>

      <h2 id="availability">9. Availability</h2>
      <p>
        We work to keep the song playing around the clock, but The Infinite is a free service and we can't guarantee
        that it is always available, without interruptions or without errors. We may pause it for maintenance, change or
        remove features, or end the service altogether; we will announce an end of the service at least 4 weeks in
        advance so you can download your data.
      </p>

      <h2 id="liability">10. Liability</h2>
      <ul>
        <li>
          We are liable without limitation for intent and gross negligence, for injury to life, body or health, under
          the German Product Liability Act (<span lang="de">Produkthaftungsgesetz</span>), and where we have given a
          guarantee.
        </li>
        <li>
          For slight negligence we are liable only if we breach an obligation whose fulfilment makes the proper use of
          the service possible in the first place and on which you may regularly rely (a cardinal obligation), and then
          only for the foreseeable damage typical of this kind of service.
        </li>
        <li>Otherwise our liability is excluded.</li>
        <li>These limits also apply to the personal liability of our representatives and helpers.</li>
      </ul>
      <p>
        Because the service is free, the statutory limits of liability for gratuitous services may also apply in our
        favour.
      </p>

      <h2 id="termination">11. Ending your use</h2>
      <ul>
        <li>
          You can delete your account at any time on the <Link href="/account">account page</Link>. Before you do, you
          can download your data.
        </li>
        <li>
          We may close an account with 2 weeks' notice by email, or immediately for good cause, in particular serious or
          repeated violations of these terms.
        </li>
        <li>
          In both cases the CC BY 4.0 licences you granted remain in effect, and loops are handled as described in
          section 4.
        </li>
      </ul>

      <h2 id="changes">12. Changes to these terms</h2>
      <p>
        We may change these terms when the service or the law changes. We will tell you about changes by email or in the
        app at least 6 weeks before they take effect. If you don't agree, you can delete your account before then.
        Changes never affect licences you have already granted.
      </p>

      <h2 id="law">13. Governing law and final provisions</h2>
      <ul>
        <li>
          These terms are governed by the law of the Federal Republic of Germany, excluding the UN Convention on
          Contracts for the International Sale of Goods. If you are a consumer, this choice of law does not deprive you
          of the protection of the mandatory law of the country where you habitually live.
        </li>
        <li>
          If you are a merchant, a legal entity under public law or a special fund under public law, the place of
          jurisdiction is Berlin.
        </li>
        <li>If any provision is invalid, the rest of these terms remain valid.</li>
        <li>These terms are written in English; if a translation is ever added, the English version prevails.</li>
      </ul>
    </LegalLayout>
  );
}
